import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Printer, FileDown } from "lucide-react";
import { LUCIDA, senderLine, longDate, LetterHead, LetterFooter, stripLeadingName } from "@/components/Letterhead";
import { toast } from "sonner";

const money = (n, cur) => `${Number(n || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${cur}`;

export default function Invoice() {
  const { id } = useParams();
  const nav = useNavigate();
  const [inv, setInv] = useState(null);
  const [settings, setSettings] = useState(null);
  const [zbusy, setZbusy] = useState(false);

  const downloadZugferd = async () => {
    setZbusy(true);
    try {
      const r = await api.get(`/invoices/${id}/zugferd`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `ZUGFeRD-${inv.invoice_number}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success("ZUGFeRD-E-Rechnung erstellt");
    } catch (e) {
      let msg = "Fehler beim Erzeugen der ZUGFeRD-Datei";
      try { const t = e?.response?.data ? JSON.parse(await e.response.data.text()) : null; if (t?.detail) msg = t.detail; } catch (parseErr) { console.debug("ZUGFeRD-Fehlerantwort nicht lesbar:", parseErr); }
      toast.error(msg);
    } finally { setZbusy(false); }
  };

  useEffect(() => { api.get(`/invoices/${id}`).then((r) => setInv(r.data)).catch(() => {}); }, [id]);
  useEffect(() => { api.get("/settings").then((r) => setSettings(r.data)).catch(() => {}); }, []);
  if (!inv) return <div className="text-muted-foreground p-8">Lädt…</div>;

  const cur = inv.currency || "EUR";
  const company = settings?.company || inv.seller || {};
  const logo = inv.seller?.logo_data_url || company.logo_data_url;
  const person = (inv.bill_to?.contact || "").trim();
  const anyCustoms = inv.lines.some((l) => l.hs_code || l.hts_code_us);

  return (
    <div>
      <div className="flex items-center justify-between mb-4 print:hidden">
        <button onClick={() => nav(-1)} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="invoice-back"><ArrowLeft className="h-4 w-4" /> Zurück</button>
        <div className="flex items-center gap-2">
          {inv.bill_to?.vat_id && <Button variant="outline" onClick={downloadZugferd} disabled={zbusy} data-testid="invoice-zugferd"><FileDown className="h-4 w-4 mr-1.5" /> ZUGFeRD (E-Rechnung)</Button>}
          <Button onClick={() => window.print()} data-testid="invoice-print"><Printer className="h-4 w-4 mr-1.5" /> Drucken / PDF</Button>
        </div>
      </div>

      <div className="mx-auto a4-page bg-white text-black" style={LUCIDA} data-testid="invoice-doc">
        <LetterHead logo={logo} company={company} />

        <div className="mt-12">
          <div className="text-[10px] text-neutral-500 underline underline-offset-2">{senderLine(company)}</div>
          <div className="mt-3 text-sm leading-6">
            <div className="font-semibold">{inv.bill_to?.name}</div>
            <div className="whitespace-pre-line">{stripLeadingName(inv.bill_to?.address, inv.bill_to?.name)}</div>
            {inv.bill_to?.vat_id && <div>USt-IdNr.: {inv.bill_to.vat_id}</div>}
          </div>
        </div>

        <div className="mt-10 flex justify-between items-baseline gap-4">
          <div className="text-lg font-bold" data-testid="invoice-title">Rechnung: {inv.invoice_number}</div>
          <div className="text-right text-sm text-neutral-700">
            <div>{longDate(inv.issued_at)}</div>
            {inv.order_number && <div className="text-xs text-neutral-500">Auftrag: {inv.order_number}</div>}
          </div>
        </div>

        <div className="mt-6 text-sm" data-testid="invoice-salutation">
          {inv.salutation || (person ? `Hallo ${person},` : "Guten Tag,")}
        </div>
        <div className="mt-2 text-sm">vielen Dank für Ihren Auftrag. Wir erlauben uns, wie folgt zu berechnen:</div>

        <table className="w-full text-sm mt-6 border-t border-neutral-400">
          <thead>
            <tr className="text-left font-bold border-b border-neutral-300">
              <th className="py-2 pr-3 text-right w-12">Pos.</th>
              <th className="py-2 pr-3 text-right w-14">Menge</th>
              <th className="py-2 pr-3">Leistungsbeschreibung</th>
              <th className="py-2 pr-3 text-right">Einzelpreis</th>
              <th className="py-2 pr-3 text-right">USt</th>
              <th className="py-2 text-right">Betrag netto</th>
            </tr>
          </thead>
          <tbody>
            {inv.lines.map((l, i) => (
              <tr key={i} className="border-b border-neutral-200 align-top">
                <td className="py-2 pr-3 text-right">{i + 1}</td>
                <td className="py-2 pr-3 text-right">{l.quantity}</td>
                <td className="py-2 pr-3">{l.name}
                  {l.quality === "second_choice" && <span className="ml-1 inline-block rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800 align-middle">2. WAHL</span>}
                  {(l.hs_code || l.hts_code_us) && <div className="text-[10px] text-neutral-400">{l.hs_code ? `HS ${l.hs_code}` : ""}{l.hs_code && l.hts_code_us ? " · " : ""}{l.hts_code_us ? `US-HTS ${l.hts_code_us}` : ""}</div>}
                </td>
                <td className="py-2 pr-3 text-right">{money(l.unit_price, cur)}</td>
                <td className="py-2 pr-3 text-right">{l.tax_rate}%</td>
                <td className="py-2 text-right">{money(l.net, cur)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="mt-5 flex justify-end">
          <div className="w-72 text-sm space-y-1">
            <div className="flex justify-between"><span className="text-neutral-600">Summe netto</span><span>{money(inv.totals.net, cur)}</span></div>
            <div className="flex justify-between"><span className="text-neutral-600">zzgl. MwSt</span><span>{money(inv.totals.tax, cur)}</span></div>
            <div className="flex justify-between border-t border-neutral-400 pt-1 text-base font-bold"><span>Gesamtbetrag</span><span>{money(inv.totals.gross, cur)}</span></div>
            {cur !== "EUR" && <div className="flex justify-between text-xs text-neutral-500 pt-1"><span>EUR-Gegenwert (Kurs {inv.exchange_rate})</span><span>{money(inv.totals_eur?.gross, "EUR")}</span></div>}
          </div>
        </div>

        {inv.tax_exemption_note && (
          <div className="mt-5 text-sm font-medium text-neutral-800" data-testid="invoice-exemption-note">
            {inv.tax_exemption_note}
          </div>
        )}

        {anyCustoms && <div className="mt-6 text-xs text-neutral-500">Ausfuhr/Zoll: HS- bzw. US-HTS-Codes je Position angegeben.</div>}
        {inv.note && <div className="mt-4 text-sm text-neutral-700">{inv.note}</div>}

        <div className="mt-8 text-sm">Herzlichen Dank für Ihr Vertrauen.</div>
        <div className="mt-1 text-base font-bold">Gluty-Free Club</div>
        <div className="text-xs italic text-neutral-500">powered by Consberry GmbH</div>

        <LetterFooter company={company} />
      </div>
    </div>
  );
}
